import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash, MagnifyingGlass, Phone, ArrowLeft } from "@phosphor-icons/react";

const STATUS_VARIANT = {
  lead: "muted", active: "success", dormant: "warning",
  lost: "danger", onboarded: "success",
};

/**
 * Prospects list page.
 *
 * Top of page = search box + status filter + New prospect button.
 * Below = a simple table of every prospect with call count + last-call date
 * + onboarded badge. Click a row to go to the detail page.
 */
export default function ProspectsList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", contact_email: "",
    phone: "", city: "", province: "", expected_monthly_volume_l: "",
    notes: "",
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects", { params: status ? { status } : {} });
      setRows(data.prospects || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load prospects");
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const ql = q.trim().toLowerCase();
    return [r.company_name, r.contact_name, r.phone, r.city, r.province, r.contact_email]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql));
  });

  const save = async () => {
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setCreating(true);
    try {
      const body = { ...form };
      // Pydantic wants a float or null — coerce empty string.
      if (!body.expected_monthly_volume_l) delete body.expected_monthly_volume_l;
      else body.expected_monthly_volume_l = parseFloat(body.expected_monthly_volume_l);
      const { data } = await api.post("/prospects", body);
      toast.success("Prospect added");
      setShowAdd(false);
      nav(`/sales-tools/crm/${data.prospect_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this prospect and all its call history?")) return;
    try {
      await api.delete(`/prospects/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <SectionPad>
      <PageHeader
        title="Prospects"
        subtitle={<Link to="/sales-tools" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={12}/> Sales Tools</Link>}
        actions={
          <button
            className="gf-btn gf-btn-primary"
            onClick={() => setShowAdd(true)}
            data-testid="new-prospect-button"
          >
            <Plus size={14}/> New prospect
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input
            className="gf-input pl-8"
            placeholder="Search company, contact, city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="prospect-search"
          />
        </div>
        <select
          className="gf-input"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="prospect-status-filter"
        >
          <option value="">All statuses</option>
          <option value="lead">Lead</option>
          <option value="active">Active</option>
          <option value="dormant">Dormant</option>
          <option value="lost">Lost</option>
          <option value="onboarded">Onboarded</option>
        </select>
        <div className="text-xs font-mono text-zinc-500 ml-auto">
          {loading ? "LOADING…" : `${filtered.length} OF ${rows.length} PROSPECTS`}
        </div>
      </div>

      <div className="gf-card">
        <div className="gf-card-body overflow-x-auto">
          <table className="gf-table text-sm">
            <thead><tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Phone</th>
              <th>City / Prov</th>
              <th>Status</th>
              <th className="text-right">Exp. volume / mo</th>
              <th className="text-right">Calls</th>
              <th>Last call</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={9} className="text-center text-zinc-400 italic py-8">No prospects yet. Click "New prospect" to add one.</td></tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.prospect_id}
                  className="cursor-pointer hover:bg-zinc-50"
                  onClick={() => nav(`/sales-tools/crm/${r.prospect_id}`)}
                  data-testid={`prospect-row-${r.prospect_id}`}
                >
                  <td>{r.company_name}</td>
                  <td>{r.contact_name || <span className="text-zinc-400">—</span>}</td>
                  <td className="font-mono">{r.phone || <span className="text-zinc-400">—</span>}</td>
                  <td>{r.city || "—"} / {r.province || ""}</td>
                  <td><Badge variant={STATUS_VARIANT[r.status] || "muted"}>{r.status}</Badge></td>
                  <td className="text-right font-mono">{r.expected_monthly_volume_l ? `${r.expected_monthly_volume_l.toLocaleString()} L` : "—"}</td>
                  <td className="text-right font-mono">{r.call_count}</td>
                  <td className="font-mono text-xs">{r.last_call_at ? r.last_call_at.slice(0, 10) : "—"}</td>
                  <td className="text-right">
                    <button
                      className="gf-btn gf-btn-ghost"
                      onClick={(e) => { e.stopPropagation(); del(r.prospect_id); }}
                      title="Delete"
                      data-testid={`delete-prospect-${r.prospect_id}`}
                    >
                      <Trash size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- Add prospect modal ---- */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !creating && setShowAdd(false)}>
          <div className="bg-white w-full max-w-lg border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Add prospect</h3></div>
            <div className="gf-card-body space-y-3">
              <div>
                <div className="overline mb-1">COMPANY NAME *</div>
                <input className="gf-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="add-company-name" autoFocus/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-1">CONTACT NAME</div>
                  <input className="gf-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="add-contact-name"/>
                </div>
                <div>
                  <div className="overline mb-1">PHONE</div>
                  <input className="gf-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-phone"/>
                </div>
              </div>
              <div>
                <div className="overline mb-1">EMAIL</div>
                <input type="email" className="gf-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="add-email"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-1">CITY</div>
                  <input className="gf-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}/>
                </div>
                <div>
                  <div className="overline mb-1">PROVINCE</div>
                  <input className="gf-input" value={form.province} maxLength={2} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })}/>
                </div>
              </div>
              <div>
                <div className="overline mb-1">EXPECTED MONTHLY VOLUME (L)</div>
                <input type="number" className="gf-input" value={form.expected_monthly_volume_l} onChange={(e) => setForm({ ...form, expected_monthly_volume_l: e.target.value })}/>
              </div>
              <div>
                <div className="overline mb-1">NOTES</div>
                <textarea className="gf-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowAdd(false)} disabled={creating}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={save} disabled={creating} data-testid="save-prospect-button">
                {creating ? "Creating…" : <><Phone size={14}/> Create & open</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionPad>
  );
}
